import { Link } from 'react-router';
import type { PhaseType } from '@beat-games/game-config';

interface SeasonStateOverlayProps {
  currentPhase: PhaseType;
  timeRemainingMs: number | null;
  phaseName: string;
  // Elimination props
  isEliminated?: boolean;
  eliminatedFromPhase?: string;
  eliminationRank?: number;
  cutoffScore?: number;
}

// Format phase name for display
function formatPhaseName(phase: string): string {
  const names: Record<string, string> = {
    QUALIFIER: 'Qualifiers',
    SHORTLIST: 'Shortlist',
    SEMIFINAL: 'Semifinals',
    FINAL: 'Finals',
  };
  return names[phase] || phase;
}

// Get qualification spots for a phase
function getQualificationSpots(phase: string): number {
  const spots: Record<string, number> = {
    QUALIFIER: 256,
    SHORTLIST: 64,
    SEMIFINAL: 16,
    FINAL: 4,
  };
  return spots[phase] || 256;
}

export default function SeasonStateOverlay({
  currentPhase,
  timeRemainingMs,
  phaseName,
  isEliminated = false,
  eliminatedFromPhase,
  eliminationRank,
  cutoffScore,
}: SeasonStateOverlayProps) {
  // Determine which state we're in
  const isSeasonClosed = currentPhase === 'CLOSED';
  const isPhaseEnding = timeRemainingMs !== null && timeRemainingMs <= 0 && !isSeasonClosed;
  const isSeasonNotStarted = timeRemainingMs === null && !isSeasonClosed && !isEliminated;

  // Don't render if season is active and running (and player is not eliminated)
  if (!isSeasonClosed && !isPhaseEnding && !isSeasonNotStarted && !isEliminated) {
    return null;
  }

  // Determine background color based on state
  const getBgGlow = () => {
    if (isEliminated) return 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(239,68,68,0.05) 0%, transparent 70%)';
    if (isSeasonClosed) return 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(239,68,68,0.03) 0%, transparent 70%)';
    return 'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(255,255,255,0.02) 0%, transparent 70%)';
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[#030303]/95 backdrop-blur-sm rounded-xl overflow-hidden z-40">
      {/* Atmospheric background */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Subtle radial glow */}
        <div
          className="absolute inset-0"
          style={{ background: getBgGlow() }}
        />

        {/* Animated scan line effect */}
        <div
          className="absolute inset-0 opacity-[0.015]"
          style={{
            backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.5) 2px, rgba(255,255,255,0.5) 4px)',
            animation: 'scanlines 8s linear infinite',
          }}
        />

        {/* Corner accents - red tinted for elimination */}
        <div className={`absolute top-4 left-4 w-8 h-8 border-t border-l ${isEliminated ? 'border-[#ef4444]/20' : 'border-white/10'}`} />
        <div className={`absolute top-4 right-4 w-8 h-8 border-t border-r ${isEliminated ? 'border-[#ef4444]/20' : 'border-white/10'}`} />
        <div className={`absolute bottom-4 left-4 w-8 h-8 border-b border-l ${isEliminated ? 'border-[#ef4444]/20' : 'border-white/10'}`} />
        <div className={`absolute bottom-4 right-4 w-8 h-8 border-b border-r ${isEliminated ? 'border-[#ef4444]/20' : 'border-white/10'}`} />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-sm">
        {/* Eliminated State */}
        {isEliminated && (
          <div className="animate-fade-in">
            {/* Icon - X mark with subtle animation */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-b from-[#ef4444]/10 to-transparent border border-[#ef4444]/20 flex items-center justify-center">
                  <svg
                    className="w-10 h-10 text-[#ef4444]/70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </div>
                {/* Fading ring effect */}
                <div
                  className="absolute inset-0 rounded-full border border-[#ef4444]/10"
                  style={{ animation: 'pulse-ring 2s ease-out infinite' }}
                />
              </div>
            </div>

            {/* Title */}
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#ef4444] tracking-[0.02em] mb-3">
              {eliminatedFromPhase ? 'Eliminated' : 'Not Qualified'}
            </h2>

            {/* Subtitle */}
            <p className="text-white/40 text-sm leading-relaxed mb-5">
              {eliminatedFromPhase
                ? `You didn't make the cut from the ${formatPhaseName(eliminatedFromPhase)} phase.`
                : 'You did not qualify for this phase of the competition.'}
            </p>

            {/* Stats card - only show if we have elimination data */}
            {(eliminationRank || (cutoffScore !== undefined && eliminatedFromPhase)) && (
              <div className="mb-6 p-4 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                {eliminationRank && (
                  <div className={cutoffScore !== undefined && eliminatedFromPhase ? 'mb-3' : ''}>
                    <div className="text-white/30 text-[10px] uppercase tracking-[0.2em] mb-1">
                      Your Final Rank
                    </div>
                    <div className="font-mono text-2xl font-bold text-white/70">
                      #{eliminationRank}
                    </div>
                  </div>
                )}

                {cutoffScore !== undefined && eliminatedFromPhase && (
                  <div className={eliminationRank ? 'pt-3 border-t border-white/[0.06]' : ''}>
                    <div className="text-white/30 text-[10px] uppercase tracking-[0.2em] mb-1">
                      Qualification Cutoff
                    </div>
                    <div className="text-white/50 text-sm">
                      Top {getQualificationSpots(eliminatedFromPhase)} qualified with{' '}
                      <span className="font-mono text-[#00ff88]">
                        {(cutoffScore / 1000).toFixed(3)}s
                      </span>
                      {' '}or better
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Encouragement message */}
            <p className="text-white/30 text-xs mb-6 italic">
              Thank you for competing. Better luck next season!
            </p>

            {/* CTA */}
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 rounded text-white/80 hover:text-white text-sm font-medium tracking-wide transition-all duration-200"
            >
              <span>View Leaderboard</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            {/* Status badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-[#ef4444]/5 border border-[#ef4444]/10 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-[#ef4444]/50" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#ef4444]/50">
                Season Over For You
              </span>
            </div>
          </div>
        )}

        {/* Season Not Started State */}
        {isSeasonNotStarted && !isEliminated && (
          <div className="animate-fade-in">
            {/* Icon */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center">
                  <svg
                    className="w-7 h-7 text-white/40"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                {/* Pulsing ring */}
                <div className="absolute inset-0 rounded-full border border-white/5 animate-ping" style={{ animationDuration: '2s' }} />
              </div>
            </div>

            {/* Title */}
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-[0.02em] mb-3">
              Season Starting Soon
            </h2>

            {/* Subtitle */}
            <p className="text-white/40 text-sm leading-relaxed mb-6">
              The competition is being prepared. Check back shortly for the {phaseName} phase to begin.
            </p>

            {/* Status indicator */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/[0.03] border border-white/[0.06] rounded-full">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#f59e0b] opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#f59e0b]" />
              </span>
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/50">
                Awaiting Start
              </span>
            </div>
          </div>
        )}

        {/* Phase Ending State */}
        {isPhaseEnding && !isEliminated && (
          <div className="animate-fade-in">
            {/* Animated loader */}
            <div className="mb-6 flex justify-center">
              <div className="relative w-16 h-16">
                {/* Outer spinning ring */}
                <div
                  className="absolute inset-0 rounded-full border border-white/10"
                  style={{
                    borderTopColor: 'rgba(245,158,11,0.5)',
                    animation: 'spin 2s linear infinite',
                  }}
                />
                {/* Inner spinning ring (opposite direction) */}
                <div
                  className="absolute inset-2 rounded-full border border-white/5"
                  style={{
                    borderBottomColor: 'rgba(245,158,11,0.3)',
                    animation: 'spin 1.5s linear infinite reverse',
                  }}
                />
                {/* Center dot */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2 h-2 rounded-full bg-[#f59e0b]/50 animate-pulse" />
                </div>
              </div>
            </div>

            {/* Title */}
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#f59e0b] tracking-[0.02em] mb-3">
              Phase Ending
            </h2>

            {/* Subtitle */}
            <p className="text-white/40 text-sm leading-relaxed mb-6">
              The {phaseName} phase has concluded. Preparing for the next stage of competition.
            </p>

            {/* Status indicator */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#f59e0b]/5 border border-[#f59e0b]/20 rounded-full">
              <div className="flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-[#f59e0b]/60 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#f59e0b]/70">
                Transitioning
              </span>
            </div>
          </div>
        )}

        {/* Season Closed State */}
        {isSeasonClosed && !isEliminated && (
          <div className="animate-fade-in">
            {/* Trophy icon */}
            <div className="mb-6 flex justify-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-full bg-gradient-to-b from-white/[0.03] to-transparent border border-white/10 flex items-center justify-center">
                  <svg
                    className="w-9 h-9 text-white/50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M16 4h2a2 2 0 012 2v1a4 4 0 01-4 4m-6 0a4 4 0 01-4-4V6a2 2 0 012-2h2m4 14v-3m-4 3h8m-4-3a4 4 0 01-4-4V4h8v7a4 4 0 01-4 4z"
                    />
                  </svg>
                </div>
                {/* Decorative sparkles */}
                <div className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-white/20 animate-pulse" />
                <div className="absolute -bottom-1 -left-1 w-1.5 h-1.5 rounded-full bg-white/15 animate-pulse" style={{ animationDelay: '0.5s' }} />
              </div>
            </div>

            {/* Title */}
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-[0.02em] mb-3">
              Season Complete
            </h2>

            {/* Subtitle */}
            <p className="text-white/40 text-sm leading-relaxed mb-6">
              Season 1 has concluded. Thank you to all participants for competing.
            </p>

            {/* CTA */}
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white/[0.05] hover:bg-white/[0.08] border border-white/10 hover:border-white/20 rounded text-white/80 hover:text-white text-sm font-medium tracking-wide transition-all duration-200"
            >
              <span>View Final Results</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>

            {/* Season ended badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-3 py-1.5 bg-white/[0.02] rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-white/30" />
              <span className="text-[10px] tracking-[0.2em] uppercase text-white/30">
                Season Ended
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Add keyframe styles */}
      <style>{`
        @keyframes scanlines {
          0% { transform: translateY(0); }
          100% { transform: translateY(4px); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-ring {
          0% { transform: scale(1); opacity: 0.3; }
          100% { transform: scale(1.3); opacity: 0; }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
